/**
 * Groups and plurals (D217) — how a pack answers when the player names
 * several things at once.
 *
 * @remarks
 * Mike's ruling, 2026-09-28: a plural names the SET. `examine open jars`
 * with four open jars in the room means all four, and asking "which one?"
 * answers a question the player did not ask. How a set is answered is the
 * builder's, declared once for the pack in `plurals`:
 *
 * - `set` — the set answers with ONE line (the demos' choice). Looking at the
 *   set reads its group's description, when every member belongs to one
 *   group; otherwise the engine names the members in one sentence
 *   (`pluralSetNamed`). Any other verb on a set is refused in one line
 *   (`pluralOneAtATime`, "One at a time."), and nothing happens to the
 *   things — Mike's second ruling of the same day.
 * - `each` — the verb runs on every member in turn, a turn apiece, exactly
 *   as if the player had typed the command once per thing.
 * - `each_up_to` — each in turn while the set is no bigger than `count`, and
 *   the set's one line beyond it.
 *
 * A GROUP is only a name for things that belong together and the words the
 * pack says about them together. Items join by `groupId`; the group's
 * `description` is conditional text like any description, so the jar house
 * can say one thing while all four jars stand open and another as they are
 * sealed. A group names no noun of its own: who the set is comes from the
 * plural the player typed, and the group only answers for it.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionalTextSchema } from './conditional-text.js';
import { groupIdSchema } from './identifiers.js';

/** One declared group — see the module remarks. */
export const groupDefinitionSchema = z
	.strictObject({
		/** What the pack says about the members together, read when the player looks at a set of them. */
		description: conditionalTextSchema,
		groupId: groupIdSchema,
	})
	.readonly();

/** One declared group — see {@link groupDefinitionSchema}. */
export type GroupDefinition = z.infer<typeof groupDefinitionSchema>;

/**
 * How a set is answered — see the module remarks. `each_up_to` needs a count
 * of at least two, because a set of one is a single thing and never reaches
 * the policy.
 */
export const pluralAnswerSchema = z.discriminatedUnion('answer', [
	z.strictObject({ answer: z.literal('set') }).readonly(),
	z.strictObject({ answer: z.literal('each') }).readonly(),
	z.strictObject({ answer: z.literal('each_up_to'), count: z.int().min(2) }).readonly(),
]);

/** How a set is answered — see {@link pluralAnswerSchema}. */
export type PluralAnswer = z.infer<typeof pluralAnswerSchema>;
